import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  ActivityIndicator,
  type LayoutChangeEvent,
  Platform,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import Animated, {useAnimatedStyle} from 'react-native-reanimated';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useHeaderHeight} from '@react-navigation/elements';
import {useFocusEffect} from '@react-navigation/native';
import {ThemeProvider} from '@rn-vui/themed';
import {FlashList, FlashListRef, ListRenderItem} from '@shopify/flash-list';
import {observer} from 'mobx-react-lite';

import ItemSeparator from '../../components/ItemSeparator';
import LoadingImg from '../../components/LoadingImg';
import {ProductTileCarousel} from '../../components/ProductTile';
import StickyFilterPillBar, {StickyFilterDropdown} from '../../components/StickyFilterBar';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {useOptionalScrollCollapseContext} from '../../context/ScrollCollapseContext';
import {useSearchState} from '../../context/SearchContext';
import {useScrollCollapseJSHandler} from '../../hooks/useScrollCollapse';
import {ScreenTypes, useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {useSearchLines} from '../../queries/search/useSearchLines';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import rewardsTheme from '../../theme/rewardsTheme';
import {OrderLine} from '../../transport/apiServiceResponse';
import {applyTierSort} from '../../utils/rewards';

const COLUMN_GAP = 8;
const CONTENT_PADDING = 8;

const RangeListEmpty = ({isFetching}: {isFetching: boolean}) =>
  isFetching ? (
    <LoadingImg />
  ) : (
    <View style={emptyStyles.container}>
      <Text style={emptyStyles.text}>NO PRODUCTS FOUND</Text>
    </View>
  );

const emptyStyles = StyleSheet.create({
  container: {justifyContent: 'flex-end', alignItems: 'center', height: 200},
  text: {fontSize: 16, fontWeight: '500', color: theme.colour.text.heading.primary},
});

const FOOTER_STYLE = StyleSheet.create({footer: {marginTop: 24}});
const RangeListFooter = ({isFetchingNextPage}: {isFetchingNextPage: boolean}) =>
  isFetchingNextPage ? (
    <View style={FOOTER_STYLE.footer}>
      <ActivityIndicator size="large" color={theme.colour.activityIndicator.primary} />
    </View>
  ) : null;

const RangeProductListScreen = observer(() => {
  const {globalStore} = useStores();
  const searchState = useSearchState();
  const searchQuery = useSearchLines();

  // Close dropdown when navigating away
  useFocusEffect(
    React.useCallback(() => {
      return () => globalStore.setFiltersVisible(false);
    }, [globalStore]),
  );
  const listRef = useRef<FlashListRef<OrderLine>>(null);
  const {isWebDesktopScreen, screenType} = useWebScreenDimensions();
  const {scrollHandler} = useScrollCollapseJSHandler(listRef);
  const headerHeight = useHeaderHeight();
  const insets = useSafeAreaInsets();
  const {height: windowHeight} = useWindowDimensions();
  const scrollCollapseCtx = useOptionalScrollCollapseContext();

  // DIS-1127: Measure the actual clip-container height at runtime. The
  // Animated.View wrapper must match this exactly plus maxCollapse so that
  // at max collapse its bottom aligns with listWrapper's bottom (the
  // overflow:hidden clip line). Using `windowHeight` instead — as the
  // previous code did — over-extends the wrapper by `windowHeight -
  // listWrapperHeight`, which on devices with a flex-laid-out native tab
  // bar + home-indicator safe area is ~106 px. That overflow is the exact
  // region FlashList's flex bottom falls into, hiding the last row's
  // "Add to cart" button.
  //
  // Measured value propagates via React state; initial render falls back to
  // `windowHeight` (the old over-extended value) so the wrapper is at least
  // as tall as the clip on the first paint before onLayout fires.
  const [listWrapperHeight, setListWrapperHeight] = useState(0);
  const onListWrapperLayout = useCallback((e: LayoutChangeEvent) => {
    setListWrapperHeight(e.nativeEvent.layout.height);
  }, []);

  // DIS-1127: Wrapper-translate architecture.
  //
  // History: the previous "absolute-positioned pill bar overlay" approach hit
  // the same class of Android native z-order bug that the existing code
  // comments at `StickyFilterBar.tsx:29-32` warn about — product tiles with
  // implicit elevation render ABOVE any absolute overlay that shares their
  // parent, defeating zIndex and `elevation` alike.
  //
  // Fix: flex-sibling layout (spacer → pill bar → FlashList) wrapped in ONE
  // Animated.View whose translateY follows header collapse. Tiles stay
  // naturally clipped inside FlashList's flex bounds (no overlay to leak
  // around), and the whole subtree slides up together as a unit.
  //
  // Geometry: at rest the wrapper extends from y=0 down; at max collapse it
  // has translated up by `maxCollapse`, so its bottom should reach exactly
  // the listWrapper's `overflow: hidden` clip line — no further, no shorter.
  const maxCollapse = Math.max(0, headerHeight - insets.top);
  const wrapperHeight = (listWrapperHeight || windowHeight) + maxCollapse;

  const wrapperAnimatedStyle = useAnimatedStyle(() => {
    'worklet';
    if (!scrollCollapseCtx) return {transform: [{translateY: 0}]};
    const ctxMaxCollapse = Math.max(0, scrollCollapseCtx.headerHeight.get() - insets.top);
    const offset = Math.min(scrollCollapseCtx.scrollY.get(), ctxMaxCollapse);
    return {transform: [{translateY: -offset}]};
  });

  const wrapperStaticStyle = useMemo(() => ({height: wrapperHeight}), [wrapperHeight]);

  const spacerStyle = useMemo(() => ({height: headerHeight}), [headerHeight]);

  // DIS-1127 short-content fix: the Animated.View wrapper is sized to
  // `listWrapperHeight + maxCollapse`, so FlashList (flex:1 inside) gets that
  // same extra `maxCollapse` in height. At rest only `listWrapperHeight`
  // minus header+pill is actually visible on screen — the bottom `maxCollapse`
  // px live behind the listWrapper's `overflow:hidden` clip line until the
  // wrapper translates up on collapse. For content tall enough to overflow
  // the visible-at-rest area but NOT tall enough to exceed FlashList's
  // full viewport (e.g. 2–3 tiles on a small search result), FlashList
  // thinks content fits, never reports a scrollable region, the wrapper
  // never translates, and the last row's bottom stays behind the clip —
  // visible as a clipped "Add to cart" button.
  //
  // Padding the contentContainer by `maxCollapse` inflates the scroll
  // content so any content ≥ visible-at-rest triggers scroll, which
  // collapses the header and exposes the clipped region. Short content
  // (fits at rest) is unaffected — the padding just becomes trailing
  // empty space below the last tile that the user never scrolls into.
  const flashListContentStyle = useMemo(
    () => ({paddingHorizontal: COLUMN_GAP / 2, paddingBottom: maxCollapse}),
    [maxCollapse],
  );

  // Scroll to top and uncollapse header when filter, sort, or search changes.
  // The search TanStack query cache stays intact — only the scroll position resets.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    listRef.current?.scrollToOffset({offset: 0, animated: false});
    if (scrollCollapseCtx) {
      scrollCollapseCtx.scrollY.set(0);
      scrollCollapseCtx.prevScrollY.set(0);
      scrollCollapseCtx.accumulatedUp.set(scrollCollapseCtx.TAB_BAR_HEIGHT);
      scrollCollapseCtx.accumulatedDown.set(0);
    }
  }, [
    searchState.sortOptionId,
    searchState.activeFacets,
    searchState.specials,
    searchState.priceRange,
    searchState.query,
    scrollCollapseCtx,
  ]);

  // DIS-1179: tier-sort applied only when default ('top_products') sort is
  // active; explicit user sort (price/name/etc.) takes precedence.
  const displayLines = useMemo(() => {
    const flat = searchQuery.data?.pages?.flatMap(p => p.data) ?? [];
    return searchState.sortOptionId === 'top_products' ? applyTierSort(flat) : flat;
  }, [searchQuery.data, searchState.sortOptionId]);
  const isLoading = searchQuery.isLoading && !searchQuery.isPlaceholderData;
  // Detect department switch to avoid stale placeholder flash (keepPreviousData
  // shows previous department's products briefly). Show loading instead.
  // IMPORTANT: Only trigger on departmentId changes — NOT keyword/sort/filter
  // changes, because unmounting StickyFilterPillBar destroys its local inputText
  // state, clearing the user's search keywords mid-typing.
  const prevDeptRef = useRef(searchState.departmentId);
  const isDepartmentSwitch =
    searchState.departmentId !== prevDeptRef.current &&
    searchQuery.isPlaceholderData &&
    searchQuery.isFetching;
  useEffect(() => {
    prevDeptRef.current = searchState.departmentId;
  }, [searchState.departmentId]);

  let numColumns = 2;

  switch (screenType) {
    case ScreenTypes.mobile:
      numColumns = 2;
      break;
    case ScreenTypes.desktop:
      numColumns = 4;
      break;
    case ScreenTypes.desktopLarge:
      numColumns = 6;
      break;
  }

  // No longer delay the entry based on index - as we're paginating the list,
  // we end up waiting ages for the new stuff to slide in.
  const renderItem = useCallback<ListRenderItem<OrderLine>>(
    ({item}) => (
      <View style={styles.tileWrapper} testID="range-product-flatlist-item-wrapper">
        <ProductTileCarousel item={item} fullWidth testContext="category" />
      </View>
    ),
    [],
  );

  return (
    <ContentView testID="shop-product-list-screen" style={styles.contentViewOverride}>
      {isLoading || isDepartmentSwitch ? (
        <LoadingImg />
      ) : (
        <View style={styles.listWrapper} onLayout={onListWrapperLayout}>
          <ThemeProvider theme={rewardsTheme}>
            {/* Wrapper Animated.View: translateY follows header collapse.
                Contains spacer + pill bar + FlashList as flex siblings so
                tiles are naturally clipped inside FlashList bounds — no
                z-order conflict with the pill bar possible. */}
            <Animated.View style={[wrapperStaticStyle, wrapperAnimatedStyle]}>
              {/* Spacer reserves the visual area occupied by the
                  transparent React Navigation header. Translates up with
                  the wrapper, so when the header collapses the spacer
                  disappears off-screen and the pill bar slides up to
                  its clamped position at safeAreaTop. */}
              <View style={spacerStyle} pointerEvents="none" />
              <StickyFilterPillBar />
              <FlashList
                key={`range-product-flashlist-${numColumns}`}
                testID="range-product-flatlist"
                data={displayLines}
                renderItem={renderItem}
                numColumns={numColumns}
                estimatedItemSize={280}
                style={[styles.list]}
                onScroll={scrollHandler}
                scrollEventThrottle={16}
                showsVerticalScrollIndicator={isWebDesktopScreen}
                showsHorizontalScrollIndicator={isWebDesktopScreen}
                onEndReachedThreshold={isWebDesktopScreen ? 0.5 : 3}
                onEndReached={() => {
                  if (searchQuery.hasNextPage && !searchQuery.isFetchingNextPage) {
                    searchQuery.fetchNextPage();
                  }
                }}
                initialNumToRender={isWebDesktopScreen ? 25 : 15}
                drawDistance={Platform.OS === 'web' ? 500 : 250}
                keyExtractor={(item, index) => `${item.id}-${index}`}
                ItemSeparatorComponent={ItemSeparator}
                ref={listRef}
                ListEmptyComponent={<RangeListEmpty isFetching={searchQuery.isFetching} />}
                ListFooterComponent={
                  <RangeListFooter isFetchingNextPage={!!searchQuery.isFetchingNextPage} />
                }
                contentContainerStyle={flashListContentStyle}
              />
            </Animated.View>
            {/* Dropdown overlay — rendered at listWrapper level (outside
                the wrapper Animated.View) so the scrim always covers the
                full visible area and its own translateY animated style
                tracks the pill bar's visual position. */}
            <StickyFilterDropdown />
          </ThemeProvider>
        </View>
      )}
    </ContentView>
  );
});

export default RangeProductListScreen;

const styles = StyleSheet.create({
  contentViewOverride: {
    paddingHorizontal: 0,
  },
  tileWrapper: {
    flex: 1,
    paddingHorizontal: COLUMN_GAP / 2,
  },
  flashListContent: {
    paddingHorizontal: COLUMN_GAP / 2,
  },
  contentContainer: {
    paddingHorizontal: CONTENT_PADDING,
  },
  list: {
    flex: 1,
  },
  listWrapper: {
    flex: 1,
    // DIS-1127: the wrapper Animated.View is sized to listWrapper height +
    // maxCollapse (see wrapperHeight above). overflow:hidden clips the
    // maxCollapse slack at rest; at max collapse the wrapper has translated
    // up by that same amount, so its content reaches exactly the clip line.
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({contain: 'layout style'} as const) : {}),
  },
});
